import React, { useState, useEffect, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './AnimalSorting.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const ALL_ANIMALS = {
  mouse: { id: 'mouse', label: 'Мышь', img: 'mouse', weightText: 'очень лёгкая', speedText: 'максимально быстрая', sizeText: 'очень маленькая', combinedText: 'очень лёгкая и очень маленькая' },
  cat: { id: 'cat', label: 'Кошка', img: 'cat', weightText: 'лёгкая', speedText: 'очень быстрая', sizeText: 'маленькая', combinedText: 'лёгкая и маленькая' },
  sheep: { id: 'sheep', label: 'Овца', img: 'sheep', weightText: 'средняя', speedText: 'средняя по скорости', sizeText: 'средняя', combinedText: 'средняя по размеру и весу' },
  horse: { id: 'horse', label: 'Лошадь', img: 'horse', weightText: 'тяжёлая', speedText: 'быстрая', sizeText: 'большая', combinedText: 'большая и тяжёлая' },
  elephant: { id: 'elephant', label: 'Слон', img: 'elephant', weightText: 'очень тяжёлый', speedText: 'медленный', sizeText: 'очень большой', combinedText: 'очень большой и очень тяжёлый' },
  gepard: { id: 'gepard', label: 'Гепард', img: 'gepard', speedText: 'очень быстрый' },
  dog: { id: 'dog', label: 'Собака', img: 'dog', speedText: 'быстрая' },
  cow: { id: 'cow', label: 'Корова', img: 'cow', speedText: 'средняя по скорости' },
  turtle: { id: 'turtle', label: 'Черепаха', img: 'turtle', speedText: 'очень медленная' },
};

const EXERCISES = [
  {
    id: 'weight',
    instruction: 'Расположи всех животных по весу: от самого лёгкого до самого тяжёлого',
    poolIds: ['mouse', 'cat', 'sheep', 'horse', 'elephant'],
    numSlots: 5,
    initialSlots: null,
    correctAnswer: ['mouse', 'cat', 'sheep', 'horse', 'elephant'],
    speakerField: 'weightText',
    hint1: 'Попробуй еще раз. Если нужна подсказка, нажми на динамик',
    hint2: null 
  },
  {
    id: 'speed',
    instruction: 'Расположи всех животных по скорости: от самого быстрого до самого медленного',
    poolIds: ['gepard', 'dog', 'cow', 'elephant', 'turtle'],
    numSlots: 5,
    initialSlots: null,
    correctAnswer: ['gepard', 'dog', 'cow', 'elephant', 'turtle'],
    speakerField: 'speedText',
    hint1: 'Попробуй еще раз. Если нужна подсказка, нажми на динамик',
    hint2: null
  },
  {
    id: 'multi_step_1',
    instruction: 'Найди двух самых лёгких животных и расставь их по размеру от маленького к большому',
    poolIds: ['mouse', 'cat', 'sheep', 'horse', 'elephant'],
    numSlots: 2,
    initialSlots: null,
    correctAnswer: ['mouse', 'cat'],
    speakerField: 'combinedText',
    hint1: 'Попробуй еще раз. Если нужна подсказка, нажми на динамик',
    hint2: 'Самые лёгкие — это мышь и кошка'
  },
  {
    id: 'multi_step_2',
    instruction: 'Теперь остальных животных расставь по размеру от самого большого до самого маленького',
    poolIds: ['sheep', 'horse', 'elephant'],
    numSlots: 5,
    initialSlots: ['mouse', 'cat', null, null, null],
    correctAnswer: ['mouse', 'cat', 'elephant', 'horse', 'sheep'],
    speakerField: 'combinedText',
    hint1: 'Попробуй еще раз. Если нужна подсказка, нажми на динамик',
    hint2: 'От большого к маленькому: слон, лошадь, овца'
  }
];

const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const AnimalSorting = ({ onBack, onComplete }) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [pool, setPool] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  
  const [isComplete, setIsComplete] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [exerciseLog, setExerciseLog] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [mistakeCount, setMistakeCount] = useState(0);
  const [hintsUsedInStep, setHintsUsedInStep] = useState(false);
  const [wrongSlots, setWrongSlots] = useState([]);

  const currentExercise = EXERCISES[currentExerciseIndex];
  const totalExercises = EXERCISES.length;

  const { play: playInstructionAudio, stop: stopInstructionAudio } = useInstructionAudio();

  const stopAll = () => {
    stopInstructionAudio();
  };

  useEffect(() => {
    if (currentExercise) {
      setPool(shuffleArray(currentExercise.poolIds));
      setSlots(currentExercise.initialSlots ? [...currentExercise.initialSlots] : Array(currentExercise.numSlots).fill(null));
      setIsComplete(false);
      setSelected(null);
      setDraggedItem(null);
      setFeedback(null);
      setMistakeCount(0);
      setHintsUsedInStep(false);
      setWrongSlots([]);
    }
  }, [currentExerciseIndex]);

  const playInstruction = useCallback(() => {
    stopAll();
    setFeedback(null);
    setWrongSlots([]);
    playInstructionAudio(currentExercise.instruction);
  }, [currentExercise, playInstructionAudio]);

  useEffect(() => {
    playInstruction();
  }, [currentExerciseIndex, playInstruction]);

  useEffect(() => {
    return () => stopAll();
  }, []);

  // Общая функция для перемещения (используется и кликом, и перетаскиванием)
  const performMove = (source, target) => {
    const newPool = [...pool];
    const newSlots = [...slots];

    let itemAId = null;
    if (source.zone === 'pool') {
      itemAId = newPool[source.index];
      newPool[source.index] = null; 
    } else {
      itemAId = newSlots[source.index];
      newSlots[source.index] = null;
    }

    // Если бросили в пустую зону пула
    if (target.zone === 'pool' && target.index === -1) {
      if (itemAId) newPool.push(itemAId);
      setPool(newPool.filter(p => p !== null));
      setSlots(newSlots);
      return;
    }

    // Если бросили на то же место
    if (source.zone === target.zone && source.index === target.index) {
      if (source.zone === 'pool') newPool[source.index] = itemAId;
      else newSlots[source.index] = itemAId;
      return;
    }

    let itemBId = null;
    if (target.zone === 'pool') {
      itemBId = newPool[target.index];
      newPool[target.index] = itemAId;
    } else {
      itemBId = newSlots[target.index];
      newSlots[target.index] = itemAId;
    }

    if (itemBId) {
      if (source.zone === 'pool') newPool[source.index] = itemBId;
      else newSlots[source.index] = itemBId;
    }

    setPool(newPool.filter(p => p !== null));
    setSlots(newSlots);
  };

  // === ЛОГИКА КЛИКОВ ===
  const handleItemClick = (zone, index, id) => {
    if (isComplete) return;
    if (zone === 'slot' && currentExercise.initialSlots && currentExercise.initialSlots[index] !== null) return;

    if (wrongSlots.length > 0 || feedback) {
      setWrongSlots([]);
      setFeedback(null);
      stopAll();
    }

    if (!selected) {
      if (id) setSelected({ zone, index });
      return;
    }

    performMove(selected, { zone, index });
    setSelected(null);
  };

  // === ЛОГИКА DRAG & DROP ===
  const handleDragStart = (e, zone, index) => {
    if (isComplete || (zone === 'slot' && currentExercise.initialSlots && currentExercise.initialSlots[index] !== null)) {
      e.preventDefault();
      return;
    }
    if (wrongSlots.length > 0 || feedback) {
      setWrongSlots([]);
      setFeedback(null);
      stopAll();
    }
    setDraggedItem({ zone, index });
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Разрешаем бросить
  };

  const handleDrop = (e, targetZone, targetIndex) => {
    e.preventDefault();
    if (!draggedItem) return;
    // Запрет бросать на заблокированные слоты
    if (targetZone === 'slot' && currentExercise.initialSlots && currentExercise.initialSlots[targetIndex] !== null) {
      setDraggedItem(null);
      return;
    }
    performMove(draggedItem, { zone: targetZone, index: targetIndex });
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleSpeakerClick = (e, text) => {
    e.stopPropagation();
    stopAll();
    setHintsUsedInStep(true);
    playInstructionAudio(text);
  };

  const handleOk = () => {
    if (isComplete || feedback || slots.includes(null)) return;
    
    const incorrectIdx = [];
    slots.forEach((id, idx) => {
      if (id !== currentExercise.correctAnswer[idx]) {
        incorrectIdx.push(idx);
      }
    });

    if (incorrectIdx.length === 0) {
      setAnswers(prev => [...prev, true]);
      setExerciseLog(prev => [...prev, { id: currentExercise.id, correct: true, hintsUsed: hintsUsedInStep }]);
      setIsComplete(true);
      setTimeout(() => nextExercise(), 3500);
    } else {
      if (mistakeCount === 0) {
        setMistakeCount(1);
        setWrongSlots(incorrectIdx);
        setFeedback('hint1');
        stopAll();
        playInstructionAudio(currentExercise.hint1);
      } else {
        setAnswers(prev => [...prev, false]);
        setExerciseLog(prev => [...prev, { id: currentExercise.id, correct: false, hintsUsed: hintsUsedInStep }]);
        setWrongSlots(incorrectIdx);

        if (currentExercise.id === 'multi_step_1') {
          setFeedback('hint2');
          stopAll();
          playInstructionAudio(currentExercise.hint2);
          setTimeout(() => nextExercise(), 4000);
        } else {
          setIsComplete(true);
          setTimeout(() => nextExercise(), 3500);
        }
      }
    }
  };

  const nextExercise = () => {
    stopAll();
    if (currentExerciseIndex < totalExercises - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    } else {
      setShowResult(true);
    }
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = answers.length > 0 ? Math.round((correctCount / answers.length) * 100) : 0;
        
        if (onComplete) {
          onComplete({
            testId: 'AnimalSorting',
            score,
            details: { total: totalExercises, correct: correctCount, exercises: exerciseLog },
          });
        } else { onBack(); }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult, answers, exerciseLog, totalExercises, onComplete, onBack]);

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate progress={answers} totalQuestions={totalExercises} forceStop={true} onComplete={onComplete || onBack}>
        <div className="as-result-content">
          <h2 className="as-result-title">🎉 Модуль завершён!</h2>
          <div className="as-result-score">
            <span className="as-score-number">{correctCount}</span>
            <span className="as-score-total"> / {totalExercises}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  // Проверка, является ли карточка перетаскиваемой в данный момент
  const isDraggingThis = (zone, index) => draggedItem?.zone === zone && draggedItem?.index === index;

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalExercises} 
      forceStop={true}
      onComplete={onComplete || onBack}
      onRepeat={playInstruction}
      extraContent={currentExercise.instruction}
      extraButtons={
        <button 
          className="as-confirm-btn" 
          onClick={handleOk} 
          disabled={isComplete || feedback !== null || slots.includes(null)}
        >
          ОК
        </button>
      }
    >
      <div className="as-game-area">
        
        {feedback && (
          <div className={`as-feedback-banner ${feedback}`}>
            {currentExercise[feedback]}
          </div>
        )}

        {/* СЛОТЫ ВВЕРХУ */}
        <div className="as-slots-row">
          {slots.map((id, index) => {
            const animal = id ? ALL_ANIMALS[id] : null;
            const isSelected = selected?.zone === 'slot' && selected?.index === index;
            const isWrong = wrongSlots.includes(index);
            const isLocked = currentExercise.initialSlots && currentExercise.initialSlots[index] !== null;
            
            return (
              <div 
                key={index} 
                className={`as-slot ${animal ? 'filled' : ''} ${isSelected ? 'selected' : ''} ${isWrong ? 'wrong' : ''} ${isLocked ? 'locked' : ''}`}
                onClick={() => !isLocked && handleItemClick('slot', index, id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'slot', index)}
              >
                {animal ? (
                  <div 
                    className="as-draggable-wrapper"
                    draggable={!isLocked}
                    onDragStart={(e) => handleDragStart(e, 'slot', index)}
                    onDragEnd={handleDragEnd}
                    style={{ opacity: isDraggingThis('slot', index) ? 0.4 : 1 }}
                  >
                    <img src={`${S3_BASE_URL}/${animal.img}.png`} alt={animal.label} className="as-item-img" />
                    <button className="as-speaker-btn" onClick={(e) => handleSpeakerClick(e, animal[currentExercise.speakerField])}>🔊</button>
                  </div>
                ) : (
                  <div className="as-slot-placeholder">?</div>
                )}
              </div>
            );
          })}
        </div>

        {/* ПУЛ ВНИЗУ */}
        <div 
          className="as-pool-container"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, 'pool', -1)} // Позволяет бросить в пустую зону пула
        >
          <div className="as-pool-row">
            {pool.length === 0 && <div className="as-pool-empty">Все карточки использованы</div>}
            {pool.map((id, index) => {
              if (!id) return null;
              const animal = ALL_ANIMALS[id];
              const isSelected = selected?.zone === 'pool' && selected?.index === index;
              return (
                <div 
                  key={index} 
                  className={`as-pool-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleItemClick('pool', index, id)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, 'pool', index)}
                >
                  <div 
                    className="as-draggable-wrapper"
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'pool', index)}
                    onDragEnd={handleDragEnd}
                    style={{ opacity: isDraggingThis('pool', index) ? 0.4 : 1 }}
                  >
                    <img src={`${S3_BASE_URL}/${animal.img}.png`} alt={animal.label} className="as-item-img" />
                    <button className="as-speaker-btn" onClick={(e) => handleSpeakerClick(e, animal[currentExercise.speakerField])}>🔊</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </ModuleTemplate>
  );
};

export default AnimalSorting;